import { useMemo } from 'react';

import { isFinanceProject, isGlobalProject } from 'config';
import { formatToDate, lodash } from 'helpers';
import { useTranslation } from 'libs/i18n';
import {
  PersonalDataSection,
  PlaceOfBirthSection,
  CitizenshipSection,
  TaxResidencySection,
  PepConnectionSection,
  ContactsSection,
  ActivitySection,
  IdentityDocumentSection,
  AdditionalDocumentsSection,
  KYCSection,
  ResidentialAddressSection,
  LegalClientHeadPositionSection,
  RegistrationAddressSection,
} from 'modules/client/common/components/sections';
import { CountryRiskField } from 'modules/client/common/feature';

import { LegalClientHeadSectionsProps } from './types';

const isGlobal = isGlobalProject();
const isFinance = isFinanceProject();

export const useLegalClientHeadSections = ({
  countries,
  countriesLoading,
  kycData,
  hideActivitySection,
  mainInfo,
  bodies,
}: LegalClientHeadSectionsProps) => {
  const { t } = useTranslation();

  const tabs = useMemo(
    () =>
      lodash.compact([
        {
          label: t('client.legal.legalClientHead.sections.main'),
          id: 'main',
          content: (
            <>
              <PersonalDataSection />
              <CitizenshipSection
                countries={countries}
                countriesLoading={countriesLoading}
              />
              <IdentityDocumentSection
                enabledDocuments
                countries={countries}
                countriesLoading={countriesLoading}
              />
              <TaxResidencySection
                countries={countries}
                countriesLoading={countriesLoading}
              />
              {isGlobal && (
                <PlaceOfBirthSection
                  countries={countries}
                  countriesLoading={countriesLoading}
                />
              )}
            </>
          ),
          formSectionKeys: [
            'personalData',
            'citizenships',
            'identityDocument',
            'taxResidences',
            'placeOfBirth',
          ],
        },
        {
          label: t('client.legal.legalClientHead.sections.headInfo'),
          id: 'headInfo',
          content: <LegalClientHeadPositionSection bodies={bodies} />,
          formSectionKeys: ['position'],
        },
        {
          label: t('client.legal.legalClientHead.sections.contacts'),
          id: 'contacts',
          content: (
            <>
              {isFinance && (
                <RegistrationAddressSection
                  countries={countries}
                  countriesLoading={countriesLoading}
                />
              )}
              <ResidentialAddressSection
                enabledFiles
                countries={countries}
                countriesLoading={countriesLoading}
              />
              <ContactsSection hidePhoneField />
            </>
          ),
          formSectionKeys: [
            'residentialAddress',
            'registrationAddress',
            'contacts',
          ],
        },
        {
          label: t('client.legal.legalClientHead.sections.documents'),
          id: 'documents',
          content: <AdditionalDocumentsSection />,
          formSectionKeys: ['additionalDocumentsFiles'],
        },
        {
          label: t('client.legal.legalClientHead.sections.riskLevel'),
          id: 'riskLevel',
          content: (
            <>
              <KYCSection kycData={kycData} />
              <PepConnectionSection notRequestInformation={isGlobal} />
            </>
          ),
          formSectionKeys: ['additionalDocumentsFiles', 'pepConnection'],
        },
        {
          label: t('client.legal.legalClientHead.sections.other'),
          id: 'other',
          content: !hideActivitySection && <ActivitySection isBaseForm />,
          formSectionKeys: ['activity'],
        },
      ]),
    [bodies, countries, countriesLoading, hideActivitySection, kycData, t]
  );

  const info = useMemo(
    () =>
      mainInfo
        ? [
            lodash.compact([
              isGlobal && {
                label: t('client.legal.legalClientHead.mainInfo.citizenship'),
                value: mainInfo.citizenship?.name,
                countryRisk: {
                  country: mainInfo.citizenship,
                  field: CountryRiskField.Citizenship,
                },
              },
              {
                label: t('client.legal.legalClientHead.mainInfo.position'),
                value: mainInfo.position,
              },
            ]),
            [
              {
                label: t('client.legal.legalClientHead.mainInfo.email'),
                value: mainInfo.email,
              },
            ],
            [
              {
                label: t('client.legal.legalClientHead.mainInfo.country'),
                value: mainInfo.country?.name,
                countryRisk: {
                  country: mainInfo.country,
                  field: mainInfo.countryRiskField,
                },
              },
            ],
            [
              {
                label: t(
                  'client.legal.legalClientHead.mainInfo.dateOfExpiration'
                ),
                value:
                  mainInfo.dateOfExpiration &&
                  formatToDate(mainInfo.dateOfExpiration),
              },
            ],
          ]
        : undefined,
    [mainInfo, t]
  );

  return { tabs, info };
};
